import { formatValue } from '../shared/DiffValueCompare.vue'
import { getFieldLabel } from '../shared/fieldLabels'

const isPlainObject = v => v && typeof v === 'object' && !Array.isArray(v)

export const IMAGE_KEYS = new Set(['logoUrl', 'faviconUrl', 'imageUrl'])

export const getColumnTitleText = (tableColumns, columnId, fallbackObj) => {
  const raw = tableColumns?.[columnId]?.columnTitle ?? fallbackObj?.columnTitle
  if (raw == null || raw === '') return ''
  return String(raw).replace(/\n/g, ' ').trim()
}

export const getColumnTitleLabel = (tableColumns, columnId, fallbackObj) => (
  getColumnTitleText(tableColumns, columnId, fallbackObj) || columnId || ''
)

const getExampleIdFromPath = path => String(path || '').match(/\/examples\/([^/]+)/)?.[1] || ''

const getExampleName = (path, oldValue, newValue, ...exampleMaps) => {
  const exampleId = getExampleIdFromPath(path)
  const readName = (entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return ''
    if (entry.exampleName == null || entry.exampleName === '') return ''
    return formatValue(entry.exampleName)
  }
  if (exampleId) {
    for (let i = 0; i < exampleMaps.length; i += 1) {
      const name = readName(exampleMaps[i]?.[exampleId])
      if (name) return name
    }
  }
  return readName(newValue) || readName(oldValue)
}

/** 新增／刪除欄位時 diff 可能沒帶 /tableColumns/ path，改用欄位物件形狀判斷 */
const isTableColumnObject = v => (
  isPlainObject(v)
  && (v.columnId !== undefined || v.columnTitle !== undefined)
  && (v.element !== undefined || v.columnTitleElement !== undefined || v.sortOrder !== undefined)
)

const toImageSrc = v => (typeof v === 'string' && v.trim() ? v.trim() : '')

export const buildImageChangeRow = (key, oldValue, newValue, { isCreate = false, isDelete = false, rowKey } = {}) => {
  const oldSrc = toImageSrc(oldValue)
  const newSrc = toImageSrc(newValue)
  if (!oldSrc && !newSrc) return null
  let mode = 'update'
  if (isCreate || (!oldSrc && newSrc)) mode = 'create'
  else if (isDelete || (oldSrc && !newSrc)) mode = 'delete'
  return {
    key: rowKey || key,
    kind: 'image',
    label: getFieldLabel(key),
    mode,
    oldSrc,
    newSrc,
  }
}

const withRowMeta = (row, diff, extra = {}) => {
  if (row == null) return null
  return {
    ...row,
    key: extra.key ?? diff.key ?? '',
    path: extra.path ?? String(diff.path || ''),
    oldValue: extra.oldValue !== undefined ? extra.oldValue : diff.oldValue,
    newValue: extra.newValue !== undefined ? extra.newValue : diff.newValue,
    rowKey: row.rowKey,
  }
}

export const compactPublishDiffRows = (block = {}) => {
  const diffs = block.diffs || []
  const blockType = block.type || 'modified'
  const tableColumns = block.tableColumns || {}
  const beforeTableColumns = block.beforeTableColumns || {}
  const examples = block.examples || {}
  const beforeExamples = block.beforeExamples || {}
  const titleByColumnId = {}
  diffs.forEach((diff) => {
    const col = (isTableColumnObject(diff?.newValue) && diff.newValue)
      || (isTableColumnObject(diff?.oldValue) && diff.oldValue)
    if (!col) return
    const columnId = col.columnId || diff.key
    const title = getColumnTitleText({ [columnId]: col }, columnId)
    if (columnId && title) titleByColumnId[columnId] = title
  })
  const resolveColumnLabel = (columnId, fallbackObj) => (
    getColumnTitleText(tableColumns, columnId, fallbackObj)
    || getColumnTitleText(beforeTableColumns, columnId, fallbackObj)
    || titleByColumnId[columnId]
    || getFieldLabel(columnId)
    || columnId
    || ''
  )

  const buildExampleRow = (diff, idx, extra = {}) => {
    const key = extra.key || diff.key || ''
    const path = String(extra.path || diff.path || '')
    const isField = extra.isField || path.startsWith('/field')
    const exampleName = getExampleName(
      path,
      diff.oldValue,
      extra.newValue !== undefined ? extra.newValue : diff.newValue,
      extra.examples,
      extra.beforeExamples,
      examples,
      beforeExamples,
    )
    const newValue = extra.newValue !== undefined ? extra.newValue : diff.newValue
    const oldValue = extra.oldValue !== undefined ? extra.oldValue : diff.oldValue
    const isCreate = oldValue === undefined && newValue !== undefined
    const isDelete = newValue === undefined && oldValue !== undefined
    let actionLabel = extra.actionLabel || '更新'
    let nameClass = ''
    if (!extra.actionLabel) {
      if (isCreate) {
        actionLabel = key === 'platforms' ? '更新' : '新增'
        if (!isField && key !== 'platforms') nameClass = 'ant-tag-green'
      } else if (isDelete) {
        actionLabel = '刪除'
        if (!isField) nameClass = 'ant-tag-red'
      }
    }
    const exampleRowBase = {
      rowKey: extra.rowKey || `example-${idx}`,
      kind: 'example',
      actionLabel,
      isField,
      exampleName,
      nameClass,
    }

    if (key === 'platforms') {
      return {
        ...exampleRowBase,
        platforms: true,
        oldValue,
        newValue,
      }
    }

    const isObject = isPlainObject(oldValue) || isPlainObject(newValue)
    if (isObject && key !== 'exampleName') {
      if (isCreate || isDelete) {
        return {
          ...exampleRowBase,
          showValue: false,
        }
      }
      return {
        ...exampleRowBase,
        actionLabel: extra.actionLabel || '更新',
        nameClass: '',
        showValue: true,
        mode: 'update',
        oldText: formatValue(oldValue?.exampleName),
        newText: formatValue(newValue?.exampleName),
      }
    }

    const oldText = formatValue(oldValue)
    const newText = formatValue(newValue)
    if (oldText === newText) return null
    let mode = 'update'
    if (isCreate) mode = 'create'
    else if (isDelete) mode = 'delete'
    return {
      ...exampleRowBase,
      showValue: true,
      mode,
      oldText,
      newText,
    }
  }

  return diffs.flatMap((diff, idx) => {
    const key = diff.key || ''
    const path = String(diff.path || '')
    const meta = extra => withRowMeta(extra, diff)

    if (key === 'content' && !path.includes('/content/content')) {
      const prefix = path.includes('/restricted') ? 'Restricted ' : ''
      let diffType = blockType || 'modified'
      if (diff.oldValue === undefined && diff.newValue !== undefined) diffType = 'added'
      else if (diff.newValue === undefined && diff.oldValue !== undefined) diffType = 'removed'
      return meta({
        rowKey: `content-${idx}`,
        kind: 'content',
        label: `${prefix}${getFieldLabel('content')}`,
        oldValue: diff.oldValue,
        newValue: diff.newValue,
        diffType,
      })
    }

    if (IMAGE_KEYS.has(key)) {
      const imageRow = buildImageChangeRow(key, diff.oldValue, diff.newValue)
      return imageRow
        ? meta({ ...imageRow, key, rowKey: `image-${idx}` })
        : null
    }

    if (key === 'docs' || key === 'excludedDocs') {
      return meta({
        rowKey: `${key}-${idx}`,
        kind: 'docs',
        fieldKey: key,
        label: getFieldLabel(key),
        oldValue: diff.oldValue,
        newValue: diff.newValue,
        emptyText: key === 'docs' ? 'ALL' : '',
      })
    }

    if (path.includes('/examples')) {
      if (key === 'examples' && isPlainObject(diff.newValue) && diff.oldValue === undefined) {
        return Object.keys(diff.newValue).map(exampleId => withRowMeta(
          buildExampleRow(
            {
              ...diff,
              key: exampleId,
              newValue: diff.newValue[exampleId],
              path: `${path.replace(/\/$/, '')}/${exampleId}`,
            },
            idx,
            {
              rowKey: `example-${idx}-${exampleId}`,
              actionLabel: '更新',
              isField: path.startsWith('/field'),
              newValue: diff.newValue[exampleId],
            },
          ),
          diff,
          {
            key: exampleId,
            path: `${path.replace(/\/$/, '')}/${exampleId}`,
            oldValue: undefined,
            newValue: diff.newValue[exampleId],
          },
        ))
      }
      return meta(buildExampleRow(diff, idx))
    }

    if (['platforms', 'excludedPlatforms', 'tablePlatformsOrder'].includes(key)) {
      return meta({
        rowKey: `${key}-${idx}`,
        kind: 'platforms',
        fieldKey: key,
        label: getFieldLabel(key),
        oldValue: diff.oldValue,
        newValue: diff.newValue,
        emptyText: key === 'platforms' ? 'ALL' : '',
      })
    }

    const looksLikeTableColumn = (
      path.includes('tableColumns')
      || isTableColumnObject(diff.oldValue)
      || isTableColumnObject(diff.newValue)
    )
    if (looksLikeTableColumn && (isPlainObject(diff.oldValue) || isPlainObject(diff.newValue))) {
      let actionLabel = '更新'
      if (diff.oldValue === undefined && diff.newValue !== undefined) {
        actionLabel = '新增'
      } else if (diff.oldValue !== undefined && diff.newValue === undefined) {
        actionLabel = '刪除'
      }
      return meta({
        rowKey: `tblcol-${idx}`,
        kind: 'tableColumnChange',
        label: '',
        actionLabel,
        colTitle: resolveColumnLabel(key, actionLabel === '刪除' ? diff.oldValue : diff.newValue),
      })
    }

    const nestedColumnId = path.match(/tableColumns\/([^/]+)\//)?.[1]
    if (nestedColumnId) {
      const oldText = formatValue(diff.oldValue)
      const newText = formatValue(diff.newValue)
      if (oldText === newText) return null
      let mode = 'update'
      if (diff.oldValue === undefined && diff.newValue !== undefined) mode = 'create'
      else if (diff.newValue === undefined && diff.oldValue !== undefined) mode = 'delete'
      return meta({
        rowKey: `tblcol-field-${idx}`,
        kind: 'scalar',
        tableColumnHeading: true,
        colTitle: resolveColumnLabel(nestedColumnId),
        label: '',
        mode,
        oldText,
        newText,
      })
    }

    if (isPlainObject(diff.oldValue) || isPlainObject(diff.newValue)) {
      return meta({
        rowKey: `obj-${idx}`,
        kind: 'objectMap',
        label: key ? resolveColumnLabel(key) : '',
        oldEntries: Object.entries(diff.oldValue || {}).map(([k, val]) => [k, formatValue(val)]),
        newEntries: Object.entries(diff.newValue || {}).map(([k, val]) => [k, formatValue(val)]),
      })
    }

    if (!key) return null

    const label = resolveColumnLabel(key)
    const columnMeta = tableColumns[key] || beforeTableColumns[key]
    if (columnMeta?.element === 'editor') {
      let diffType = blockType || 'modified'
      if (diff.oldValue === undefined && diff.newValue !== undefined) diffType = 'added'
      else if (diff.newValue === undefined && diff.oldValue !== undefined) diffType = 'removed'
      return meta({
        rowKey: `content-${key}-${idx}`,
        kind: 'content',
        label,
        oldValue: diff.oldValue,
        newValue: diff.newValue,
        diffType,
      })
    }

    const oldText = formatValue(diff.oldValue)
    const newText = formatValue(diff.newValue)
    if (oldText === newText) return null

    let mode = 'update'
    if (diff.oldValue === undefined && diff.newValue !== undefined) mode = 'create'
    else if (diff.newValue === undefined && diff.oldValue !== undefined) mode = 'delete'
    return meta({
      rowKey: `${key}-${idx}`,
      kind: 'scalar',
      label,
      mode,
      oldText,
      newText,
    })
  }).filter(Boolean)
}
